import { faStar } from '@fortawesome/duotone-light-svg-icons/faStar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AvatarProps, Avatar as MUIAvatar } from '@mui/material';
import React, { CSSProperties } from 'react';
import { type UserDetailsFragment } from '../../../graphql/types';
import { BLACK2, BORDER_GRAY, WHITE } from '../../../theme';
import Text, { TextColor, TextKind, textStyleFrom } from '../Text';

const duotoneColors = {
  '--fa-primary-color': WHITE,
  '--fa-secondary-color': BLACK2,
  '--fa-primary-opacity': '1',
  '--fa-secondary-opacity': '1',
} as CSSProperties;

export enum AvatarTag {
  Star = 'STAR',
  RoundedSquare = 'ROUNDED_SQUARE',
  None = 'NONE',
}

export type PhotoUserFragment = Pick<
  UserDetailsFragment,
  'avatar' | 'firstName' | 'lastName' | 'id' | 'fitCoach'
> & {
  isStarred?: UserDetailsFragment['isStarred'];
};

interface Props extends AvatarProps {
  size: number;
  user?: PhotoUserFragment | null;
  tag?: AvatarTag;
}

const Avatar: React.FC<Props> = ({ size, user, tag, sx, ...rest }) => {
  const { firstName, lastName, avatar: photo } = user ?? {};
  const preview = photo?.preview;
  const previewUrl = preview ? `url(${preview})` : undefined;

  const getInitials = () => {
    if (firstName && lastName) {
      return `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`;
    }

    if (firstName) {
      return firstName.slice(0, 1);
    }

    if (lastName) {
      return lastName.slice(0, 1);
    }

    return 'N/A';
  };

  const initials = getInitials();

  return (
    <div style={{ position: 'relative' }}>
      <MUIAvatar
        {...rest}
        sx={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: BORDER_GRAY,
          backgroundImage: previewUrl,
          backgroundRepeat: 'no-repeat',
          backgroundSize: '100% 100%',
          borderWidth: 0,
          borderStyle: 'solid',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx,
        }}
        data-testid="avatar"
        data-itemid={user?.id}
      >
        {photo ? (
          <img
            alt={`${firstName} ${lastName}'s avatar`}
            src={`${photo?.basePath}/${photo?.id}/${size}x${size}.jpg`}
          />
        ) : (
          <Text
            style={{
              ...textStyleFrom(TextKind.LabelM, TextColor.Secondary),
              fontSize: `${size / 2.5}px`,
            }}
          >
            {initials}
          </Text>
        )}
      </MUIAvatar>
      {tag === AvatarTag.Star && (
        <FontAwesomeIcon
          style={{
            position: 'absolute',
            right: '5%',
            bottom: '5%',
            height: size / 3.2,
            width: size / 3.2,
            ...duotoneColors,
          }}
          icon={faStar}
          data-testid="avatar-star"
        />
      )}
      {tag === AvatarTag.RoundedSquare && (
        <div
          data-testid="avatar-rounded-square"
          style={{
            position: 'absolute',
            right: '0%',
            bottom: '0%',
            height: size / 4.2,
            width: size / 4.2,
            borderRadius: '4px',
            border: `2px solid ${WHITE}`,
            backgroundColor: BLACK2,
          }}
        />
      )}
    </div>
  );
};

export default Avatar;
